import { NEXT_PUBLIC_SITE_URL } from '@src/config/env'
import type { TRegionDetail } from '@src/config/localisation/locale-config.types'
import { appendQuerySuffix } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

const getPathNameWithoutLeadingSlash = (pathname: string) => {
  return pathname.startsWith('/') ? pathname.substring(1) : pathname
}

export const getLocalePath = (locale: string, pathnameWithoutLeadingSlash: string) => {
  switch (locale) {
    case 'en-us':
      return pathnameWithoutLeadingSlash // No locale prefix for US
    case 'en-ca':
      return `ca/${pathnameWithoutLeadingSlash}`
    case 'fr-ca':
      return `fr-ca/${pathnameWithoutLeadingSlash}`
    case 'en-gb':
      // UK URLs use the region display code `/uk` (regionCode is `gb`), kept consistent
      // with canonical/og and the actual routing prefix per ULU-197.
      return `uk/${pathnameWithoutLeadingSlash}`
    default:
      return pathnameWithoutLeadingSlash
  }
}

export const getAlternateTags = (
  isI18nPreviewEnabled: boolean,
  pathname?: string,
  regionDetails?: TRegionDetail,
  alternateUrls?: Record<string, string> | number[],
  // Applied v2 filters suffix (already gated on isV2Format by the caller). Appended to every
  // alternate + x-default so a filtered self-canonical page emits a filtered, reciprocal
  // hreflang cluster instead of pointing crawlers at the unfiltered URLs. No-op when absent.
  filterSuffix?: string,
) => {
  if (!isI18nPreviewEnabled || !pathname || !regionDetails) return null

  const pathnameWithoutLeadingSlash = getPathNameWithoutLeadingSlash(pathname)

  /**
   * PL flow: alternateUrls is Record<lang, url> from proto seoData.alternates.
   * Renders <link rel="alternate" hreflang href=...> per entry and adds x-default
   * (prefer alternateUrls['x-default'] ?? alternateUrls['en-us'] ?? site URL).
   */
  if (alternateUrls && !Array.isArray(alternateUrls)) {
    const localeTags = Object.entries(alternateUrls)
      .filter(([lang, url]) => Boolean(lang) && Boolean(url))
      .map(([lang, url]) => (
        <link
          key={lang.toLowerCase()}
          rel="alternate"
          hrefLang={lang.toLowerCase()}
          href={appendQuerySuffix(url, filterSuffix)}
        />
      ))

    const xDefaultHref = appendQuerySuffix(
      alternateUrls['x-default'] ?? alternateUrls['en-us'] ?? `${NEXT_PUBLIC_SITE_URL}/${pathnameWithoutLeadingSlash}`,
      filterSuffix,
    )
    localeTags.push(<link key="x-default" rel="alternate" hrefLang="x-default" href={xDefaultHref} />)
    return localeTags
  }

  if (Array.isArray(alternateUrls)) {
    const localeTags: JSX.Element[] = []

    alternateUrls.forEach(regionId => {
      const region = Object.values(regionDetails.regions).find(r => r.regionId.toString() === regionId.toString())

      if (region) {
        const supportedLocales = region.supportedLanguagesIsoCodes

        supportedLocales.forEach(locale => {
          const hreflang = locale.toLowerCase()
          const localePath = getLocalePath(locale, pathnameWithoutLeadingSlash)
          const url = appendQuerySuffix(`${NEXT_PUBLIC_SITE_URL}/${localePath}`, filterSuffix)

          localeTags.push(<link key={locale} rel="alternate" hrefLang={hreflang} href={url} />)
        })
      }
    })

    localeTags.push(
      <link
        key="x-default"
        rel="alternate"
        hrefLang="x-default"
        href={appendQuerySuffix(`${NEXT_PUBLIC_SITE_URL}/${pathnameWithoutLeadingSlash}`, filterSuffix)}
      />,
    )

    return localeTags
  }

  return null
}
